import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { getDefaultSorting, InfiniteScrollTable, YColumnsType } from '@ydistri/ds';
import { EntityListItemResponse } from '@ydistri/api-sdk';
import { useColumnWidth } from '../../../../../hooks/useColumnWidth';
import { GetLatestEntityListItemsRequest, useGetEntityListItemsQuery } from '../../apiEntityLists';
import {
  EntityListContentItem,
  EntityListTypeSupported,
  getEntityListSpecification,
} from '../../entityListsLib';
import { capitalizeFirstLetter } from '@ydistri/generic-utils';
import ScrolledTableWrapper from '../../../../../components/global/ScrolledTableWrapper/ScrolledTableWrapper';
import {
  EntityColumns,
  EntityColumnsConfig,
  useProductTableColumnsProvider,
  useStoreTableColumnsProvider,
} from '../../../../../hooks/useEntityTableColumnsProvider';
import OpenEntityListItemLinkButton from './OpenEntityListItemLinkButton';
import { computeRemSize } from '@ydistri/theme';

interface EntityListGridProps {
  entityListType: EntityListTypeSupported;
  selectedEntityListId: number;
}

const BLOCK_SIZE = 45;
const TABLE_HEIGHT = 480;

const EntityListGrid: React.FC<EntityListGridProps> = ({
  entityListType,
  selectedEntityListId,
}) => {
  const { productIdColumnWidth, storeIdColumnWidth } = useColumnWidth();
  const entityListImpl = getEntityListSpecification(entityListType);

  const storeColumnsProvider = useStoreTableColumnsProvider();
  const productColumnsProvider = useProductTableColumnsProvider();

  const columns: YColumnsType<EntityListItemResponse>[] = useMemo(() => {
    const tmpColumns: YColumnsType<EntityListItemResponse>[] = [];
    const contentStructure = entityListImpl.getContentTableStructure();

    if (contentStructure.includes(EntityListContentItem.PRODUCT)) {
      const productObjectPath = entityListImpl
        .getContentObjectPath(EntityListContentItem.PRODUCT)
        .map(capitalizeFirstLetter)
        .join('/');

      const productColumnsConfig: EntityColumnsConfig<EntityListItemResponse> = {
        [EntityColumns.ID]: {
          width: productIdColumnWidth,
          apiColumnName: `${productObjectPath}/CustomerId`,
          apiFilterable: true,
        },
        [EntityColumns.NAME]: {
          width: '29rem',
          apiColumnName: `${productObjectPath}/Name`,
          apiFilterable: true,
        },
        [EntityColumns.CODE]: {
          width: '6rem',
          apiColumnName: `${productObjectPath}/Code`,
          apiFilterable: true,
        },
      };

      const productColumns = productColumnsProvider(
        productColumnsConfig,
        entityListImpl.getContentObjectPath(EntityListContentItem.PRODUCT),
      );
      tmpColumns.push(...productColumns);
    }

    if (contentStructure.includes(EntityListContentItem.STORE)) {
      const objectPath = entityListImpl
        .getContentObjectPath(EntityListContentItem.STORE)
        .map(capitalizeFirstLetter)
        .join('/');

      const columnsConfig: EntityColumnsConfig<EntityListItemResponse> = {
        [EntityColumns.ID]: {
          width: storeIdColumnWidth,
          apiColumnName: `${objectPath}/CustomerStoreId`,
          apiFilterable: true,
        },
        [EntityColumns.NAME]: {
          width: '29rem',
          apiColumnName: `${objectPath}/Name`,
          apiFilterable: true,
        },
        [EntityColumns.CODE]: {
          width: '6rem',
          apiColumnName: `${objectPath}/Code`,
          apiFilterable: true,
        },
      };
      const storeColumns = storeColumnsProvider(
        columnsConfig,
        entityListImpl.getContentObjectPath(EntityListContentItem.STORE),
      );
      tmpColumns.push(...storeColumns);
    }

    if (contentStructure.includes(EntityListContentItem.QUANTITY)) {
      const objectPath = entityListImpl.getContentObjectPath(EntityListContentItem.QUANTITY);
      const apiObjectPath = entityListImpl
        .getContentObjectPath(EntityListContentItem.QUANTITY)
        .map(capitalizeFirstLetter)
        .join('/');

      tmpColumns.push({
        title: 'Quantity',
        key: 'quantity',
        dataIndex: objectPath,
        width: '8rem',
        align: 'right',
        apiColumnName: apiObjectPath,
        apiFilterable: true,
        apiFilterType: 'number',
        sorter: true,
      });
    }

    tmpColumns.push({
      title: '',
      width: '3rem',
      render: (_, record: EntityListItemResponse) => {
        return <OpenEntityListItemLinkButton item={record} />;
      },
    });

    return tmpColumns;
  }, [
    entityListImpl,
    productColumnsProvider,
    productIdColumnWidth,
    storeColumnsProvider,
    storeIdColumnWidth,
  ]);

  const defaultParams = useMemo(() => {
    return {
      entityListId: selectedEntityListId,
      skip: 0,
      top: BLOCK_SIZE,
      sortings: getDefaultSorting(columns),
    };
  }, [columns, selectedEntityListId]);

  const [params, setParams] = useState<GetLatestEntityListItemsRequest>(defaultParams);

  useEffect(() => {
    setParams(defaultParams);
  }, [defaultParams]);

  const { data, isFetching } = useGetEntityListItemsQuery(params);

  const rowKeyProvider = useCallback(
    (row: EntityListItemResponse) =>
      row.sku?.skuId ? `sku-${row.sku.skuId}` : `s-${row.store?.id}-p-${row.product?.id}`,
    [],
  );

  return (
    <ScrolledTableWrapper offset={80}>
      <InfiniteScrollTable<EntityListItemResponse, GetLatestEntityListItemsRequest>
        id="requestListContent"
        height={computeRemSize(TABLE_HEIGHT)}
        setParams={setParams}
        columns={columns}
        dataSource={data}
        loading={isFetching}
        rowKey={rowKeyProvider}
      />
    </ScrolledTableWrapper>
  );
};

export default EntityListGrid;
